import { useState, useEffect, useRef } from 'react';
import {
  createCommandActions,
  fuzzySearchActions,
  type CommandAction,
} from '@/lib/command-palette-actions';
import { router } from '@/lib/router';

interface CommandPaletteProps {
  show: boolean;
  onClose: () => void;
}

/**
 * FooterHint - Keyboard shortcuts hint
 * Extracted component to reduce JSX nesting depth
 */
function FooterHint() {
  const kbdClass = 'px-1.5 py-0.5 bg-slate-800 rounded';
  return (
    <div className="px-4 py-3 border-t border-slate-700 bg-slate-900/50">
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>
          Use <kbd className={kbdClass}>↑</kbd> <kbd className={kbdClass}>↓</kbd> to navigate
        </span>
        <span>
          <kbd className={kbdClass}>↵</kbd> to select
        </span>
      </div>
    </div>
  );
}

/**
 * Command Palette
 *
 * Quick navigation and actions via Cmd+K / Ctrl+K
 * - Fuzzy search across all actions
 * - Keyboard navigation (↑↓ arrows, Enter, Escape)
 * - Mobile-friendly with touch targets
 * - Grouped by category
 */
export function CommandPalette({ show, onClose }: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const allActions = createCommandActions((path) => {
    router.navigate(path);
  });
  const filteredActions = fuzzySearchActions(allActions, query);

  // Auto-focus input when opened
  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
  }, [show]);

  // Reset state when closed
  useEffect(() => {
    if (!show) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [show]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredActions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredActions[selectedIndex]) {
        executeAction(filteredActions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const executeAction = (action: CommandAction) => {
    action.action();
    onClose();
  };

  // Group actions by category
  const groupedActions = filteredActions.reduce(
    (acc, action) => {
      if (!acc[action.category]) {
        acc[action.category] = [];
      }
      acc[action.category].push(action);
      return acc;
    },
    {} as Record<string, CommandAction[]>
  );

  if (!show) return null;

  return (
    <>
      {/* Overlay - click to close */}
      <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" onClick={onClose} />

      {/* Command Palette Modal */}
      <div className="fixed top-[15vh] left-1/2 -translate-x-1/2 w-full max-w-2xl mx-4 z-50">
        <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="p-4 border-b border-slate-700">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(0); // Reset selection on new search
                }}
                onKeyDown={handleKeyDown}
                placeholder="Type a command or search..."
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg
                          text-white placeholder-slate-400 focus:outline-none focus:ring-2
                          focus:ring-blue-500 focus:border-transparent"
                data-testid="command-palette-input"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                ESC to close
              </div>
            </div>
          </div>

          {/* Results List */}
          <div
            ref={listRef}
            className="max-h-[60vh] overflow-y-auto"
            data-testid="command-palette-results"
          >
            {filteredActions.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No results found for "{query}"</div>
            ) : (
              Object.entries(groupedActions).map(([category, actions]) => (
                <div key={category} className="py-2">
                  {/* Category Header */}
                  <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {category}
                  </div>

                  {/* Actions in this category */}
                  {actions.map((action) => {
                    const index = filteredActions.indexOf(action);
                    const isSelected = index === selectedIndex;

                    return (
                      <button
                        key={action.id}
                        onClick={() => executeAction(action)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left
                                  transition-colors min-h-[44px] touch-manipulation ${
                                    isSelected
                                      ? 'bg-blue-600 text-white'
                                      : 'hover:bg-slate-700 text-slate-100'
                                  }`}
                        data-testid={`command-action-${action.id}`}
                      >
                        <span className="text-xl">{action.icon}</span>
                        <span className="flex-1 font-medium">{action.label}</span>
                        {isSelected && <span className="text-xs opacity-75">↵ Enter</span>}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer hint */}
          <FooterHint />
        </div>
      </div>
    </>
  );
}
